// THE STOCK-ON-HAND GRID. QUANTITIES ONLY.
//
// ⚠️ WHAT IS ABSENT HERE IS ABSENT ON PURPOSE, AND PERMANENTLY.
//
// This table used to carry eleven columns over a generated ledger. Six of them
// had no source and are gone -- not "not yet", but never, because the stock
// seam has no way to say them:
//
//   Unit Cost / Stock Value   No cost, price or value crosses this seam. See
//                             addons/anixify_cpq/models/stock.py: a warehouse
//                             cost figure looks authoritative and would sit
//                             ONE JOIN from a quote line, and the first person
//                             to sum it into a margin would find no test had
//                             failed. A grep test pins the absence on both
//                             sides now.
//   The allocation columns    The seam reports a quantity per product per
//   and the reorder point     place and nothing else. There is no reservation
//                             data in this system at all.
//   Status pill               Its two informative states were built from those
//                             absent fields. What remained restated the number
//                             beside it.
//
// ⚠️ AND THE HEADING IS NOT "AVAILABLE". In this industry that means
// available-to-promise -- on hand minus what is already spoken for -- and this
// system holds no allocation data whatsoever. A salesperson reading
// "Available: 14" promises 14 units that may already be gone. The wire field
// is still qty_available; this is the display contract, and it is not
// cosmetic.

import { useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { InventoryRow } from "@/services/inventoryRows";
import { CategoryPill } from "@/components/CatalogTable";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const ROW_HEIGHT = heights.row;
const HEADER_HEIGHT = heights.control;

type ColDef = {
  field: "partNo" | "description" | "brand" | "category" | "location" | "qty" | "source";
  header: string;
  width: number;
  align: "left" | "right";
  mono: boolean;
  frozen: boolean;
};

const COLS: ColDef[] = [
  { field: "partNo",      header: "Part No.",           width: 160, align: "left",  mono: false, frozen: true  },
  { field: "description", header: "Description",        width: 300, align: "left",  mono: false, frozen: true  },
  { field: "brand",       header: "Brand",              width: 120, align: "left",  mono: false, frozen: false },
  { field: "category",    header: "Category",           width: 120, align: "left",  mono: false, frozen: false },
  { field: "location",    header: "Location",           width: 170, align: "left",  mono: false, frozen: false },
  { field: "qty",         header: "On Hand (Reported)", width: 170, align: "right", mono: true,  frozen: false },
  { field: "source",      header: "Source",             width: 120, align: "left",  mono: false, frozen: false },
];

function frozenLefts(): number[] {
  const out: number[] = [];
  let acc = 0;
  for (const c of COLS) {
    if (c.frozen) { out.push(acc); acc += c.width; }
    else out.push(-1);
  }
  return out;
}

/** The quantity cell, and the whole absent-is-not-zero rule in one place.
 *
 *  FOUR RENDERINGS FOR FOUR FACTS. The reported-zero and the unreported must
 *  never look alike, and neither may be shown before the read has settled:
 *
 *    * still reading      -> "…". ⚠️ NOT "Not reported": the read is in
 *                            flight, so "nobody has reported on this" is a
 *                            statement about the world that has not been
 *                            established yet. Saying it early is the same
 *                            class of error as saying 0 -- a claim ahead of
 *                            the evidence, just a quieter one.
 *    * a positive figure  -> the number
 *    * a reported 0       -> "0", styled as the real claim it is: somebody
 *                            looked and there is none
 *    * unreported         -> "Not reported", muted. NO NUMBER AT ALL, because
 *                            printing 0 here would be this app inventing the
 *                            one value it is least allowed to.
 *
 *  `data-state` carries the distinction to the DOM so a test asserts the fact
 *  rather than the styling. */
export function QuantityCell({ row, pending }: { row: InventoryRow; pending?: boolean }) {
  const reported = row.qtyOnHand !== undefined;
  // A figure already on screen stays while a re-read is in flight -- blanking
  // it would lose a fact we hold in order to say we are fetching it again.
  const loading = !reported && !!pending;

  return (
    <span
      data-testid={`inventory-qty-${row.product.partNo}`}
      data-state={loading ? "loading" : reported ? "reported" : "unreported"}
      title={
        loading
          ? "Reading stock levels…"
          : reported
            ? "Reported on-hand quantity. Not available-to-promise: no allocations are known to this system."
            : "Not reported — no source has told this system anything about this product."
      }
      style={{
        fontVariantNumeric: "tabular-nums",
        fontWeight: reported ? typography.fontWeight.semibold : typography.fontWeight.regular,
        fontSize: reported ? typography.fontSize.sm : typography.fontSize.xs,
        color: !reported
          ? semanticColors.textPlaceholder
          : row.qtyOnHand === 0
            ? colors.red600
            : semanticColors.textPrimary,
      }}
    >
      {loading ? "…" : reported ? String(row.qtyOnHand) : "Not reported"}
    </span>
  );
}

export function InventoryTable({
  rows,
  height,
  onRowClick,
  selectedKey,
  stockPending,
}: {
  rows: InventoryRow[];
  height: number;
  onRowClick: (row: InventoryRow) => void;
  selectedKey?: string;
  /** The stock read is in flight. Quantities not yet known read as "…"
   *  rather than "Not reported" -- see QuantityCell. */
  stockPending?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  const totalWidth = useMemo(() => COLS.reduce((s, c) => s + c.width, 0), []);
  const lefts = useMemo(() => frozenLefts(), []);

  return (
    <div
      data-testid="inventory-table-scroll"
      ref={scrollRef}
      style={{
        height, overflow: "auto",
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        backgroundColor: semanticColors.surface, position: "relative", outline: "none",
      }}
    >
      <div style={{ width: totalWidth, position: "relative" }}>
        <div
          data-testid="inventory-table-head"
          style={{
            position: "sticky", top: 0, zIndex: 3, display: "flex",
            height: HEADER_HEIGHT, backgroundColor: semanticColors.panelBg,
            borderBottom: `1px solid ${semanticColors.border}`,
          }}
        >
          {COLS.map((c, idx) => (
            <div
              key={c.field}
              title={
                c.field === "qty"
                  ? "The quantity a source reported as on hand. NOT available-to-promise: no allocations are known to this system."
                  : undefined
              }
              style={{
                width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
                display: "flex", alignItems: "center",
                justifyContent: c.align === "right" ? "flex-end" : "flex-start",
                fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium,
                color: semanticColors.textSecondary,
                textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
                whiteSpace: "nowrap", backgroundColor: semanticColors.panelBg,
                position: c.frozen ? "sticky" : undefined,
                left: c.frozen ? lefts[idx] : undefined,
                zIndex: c.frozen ? 4 : 3,
                borderRight: c.frozen && idx === 1 ? `1px solid ${semanticColors.border}` : undefined,
              }}
            >
              {c.header}
            </div>
          ))}
        </div>

        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {virtualizer.getVirtualItems().map((v) => {
            const row = rows[v.index];
            if (!row) return null;
            const isSelected = row.key === selectedKey;
            const cellBg = isSelected ? semanticColors.hoverBg : "inherit";

            return (
              <div
                key={row.key}
                data-testid={`inventory-row-${row.key}`}
                className="material-row"
                onClick={() => onRowClick(row)}
                style={{
                  position: "absolute", top: 0, left: 0,
                  transform: `translateY(${v.start}px)`,
                  height: ROW_HEIGHT, width: totalWidth, display: "flex",
                  borderBottom: `1px solid ${semanticColors.border}`,
                  backgroundColor: isSelected ? semanticColors.hoverBg : semanticColors.surface,
                  cursor: "pointer",
                }}
              >
                {COLS.map((c, idx) => {
                  let content: React.ReactNode;
                  let color: string = semanticColors.textPrimary;
                  let weight: number = typography.fontWeight.regular;

                  if (c.field === "qty") {
                    content = <QuantityCell row={row} pending={stockPending} />;
                  } else if (c.field === "category") {
                    // ⚠️ A DASH, NOT A GUESS. Null is a real state on the real
                    // master: a product nobody has classified yet.
                    content = row.product.category
                      ? <CategoryPill cat={row.product.category} />
                      : <span style={{ color: semanticColors.textPlaceholder }}>—</span>;
                  } else if (c.field === "brand") {
                    content = row.product.brand ?? "—";
                    if (!row.product.brand) color = semanticColors.textPlaceholder;
                  } else if (c.field === "location") {
                    // ⚠️ NULL IS NOT "ALL". A figure the source did not
                    // attribute to a place is exactly that, and naming it
                    // would invent a warehouse that sorts and filters like a
                    // real one.
                    content = row.location ?? "Not stated";
                    if (!row.location) color = semanticColors.textPlaceholder;
                  } else if (c.field === "source") {
                    content = row.source ?? "—";
                    color = semanticColors.textMuted;
                  } else if (c.field === "partNo") {
                    content = row.product.partNo;
                    weight = typography.fontWeight.medium;
                  } else {
                    content = row.product.description;
                  }

                  return (
                    <div
                      key={c.field}
                      style={{
                        width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
                        display: "flex", alignItems: "center",
                        justifyContent: c.align === "right" ? "flex-end" : "flex-start",
                        fontSize: typography.fontSize.sm,
                        color, fontWeight: weight,
                        fontVariantNumeric: c.mono ? "tabular-nums" : "normal",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        position: c.frozen ? "sticky" : undefined,
                        left: c.frozen ? lefts[idx] : undefined,
                        zIndex: c.frozen ? 1 : 0,
                        backgroundColor: cellBg,
                        borderRight: c.frozen && idx === 1 ? `1px solid ${semanticColors.border}` : undefined,
                      }}
                    >
                      {content}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {rows.length === 0 && (
            <div
              data-testid="inventory-empty"
              style={{
                position: "absolute", top: 0, left: 0, right: 0,
                padding: spacing[40], textAlign: "center",
                fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder,
              }}
            >
              No products match the current filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
